import {Injectable, OnDestroy} from '@angular/core';
import {IdentifyList} from '../../interfaces/IdentifyList';
import {IdentifyDisplay} from '../../interfaces/IdentifyDisplay';
import {MapService} from '../map/map.service';
import {GridService} from '../grid/grid.service';
import {HttpClient} from '@angular/common/http';
import {QueryObject} from '../../interfaces/query';
import {QueryService} from '../query/query.service';
import {IdentifyParseService} from './identify-parse.service';
import {GraphicsService} from '../graphics/graphics.service';
import {GoogleAnalyticsService} from '../googleAnalytics/google-analytics.service';
import {BehaviorSubject} from 'rxjs';
import {TableOfContentsService} from '../toc/table-of-contents.service';

/**
 * Keeps track of identify objects
 * Formats and sends queries to the query service
 */
@Injectable({
  providedIn: 'root'
})
export class IdentifyService implements OnDestroy {
  public blankIdentify = true;
  private identifyNumber = 0;
  private identifyRun = false;
  private identifyIndex = 0;
  private subIndex = 0;
  private loading = false;
  private graphicsLayer;
  private highlightLayer;
  private identifyObs;
  public identifyArr: IdentifyList[] = [];
  public identifyRelatedDisplay: IdentifyDisplay[] = [];
  public identifyRunObs = new BehaviorSubject(null);
  public identifyDisplay: IdentifyDisplay = {
    attributes: [],
    columns: {
      names: [],
      aliases: [],
      types: []
    },
    headers: []
  };
  private highlightStyle = {
    point: {
      color: {r: 222, g: 98, b: 38, a: 0.3},
      size: 10,
      style: 'circle',
      outline: {
        color: {r: 255, g: 0, b: 0, a: 1.0},
        width: 2,
        style: 'solid'
      }
    },
    polyline: {
      color: {r: 255, g: 0, b: 0, a: 1.0},
      width: 1.5,
      style: 'solid'
    },
    polygon: {
      color: {r: 222, g: 98, b: 38, a: 0.3},
      style: 'none',
      outline: {
        color: {r: 255, g: 0, b: 0, a: 1.0},
        style: 'solid',
        width: 1.5
      }
    }
  };
  private selectStyle = {
    point: {
      color: {r: 23, g: 164, b: 221, a: 0.1},
      size: 10,
      style: 'circle',
      outline: {
        color: {r: 23, g: 164, b: 221, a: 1.0},
        width: 2,
        style: 'solid'
      }
    },
    polyline: {
      color: {r: 23, g: 164, b: 221, a: 1.0},
      width: 1.5,
      style: 'solid'
    },
    polygon: {
      color: {r: 0, g: 0, b: 0, a: 0.1},
      style: 'none',
      outline: {
        color: {r: 23, g: 164, b: 221, a: 1.0},
        style: 'solid',
        width: 1.5
      }
    }
  };

  constructor(private map: MapService, private grid: GridService, private parse: IdentifyParseService, private ga: GoogleAnalyticsService,
              private http: HttpClient, private query: QueryService,
              private graphics: GraphicsService, private toc: TableOfContentsService) {}

  public getBlankIdentify() { return this.blankIdentify; }
  public getLoading() { return this.loading; }
  public getIdentifyArr() { return this.identifyArr; }
  public getIdentifyDisplay() { return this.identifyDisplay; }
  public getIdentifyRelatedDisplay() { return this.identifyRelatedDisplay; }
  public setIdentifyDisplay(identifyDisplay) { this.identifyDisplay = identifyDisplay; }
  public setIdentifyRelatedDisplay(val) { this.identifyRelatedDisplay = val; }
  public getIdentifyRunObs() { return this.identifyRunObs.asObservable(); }
  public setIdentifyRunObs() { this.identifyRunObs.next(null); }
  public removeGraphics() {
    if (this.graphicsLayer) {
      this.graphics.removeGraphicsLayer(this.graphicsLayer);
      this.graphicsLayer = null;
    }
    if (this.highlightLayer) {
      this.graphics.removeGraphicsLayer(this.highlightLayer);
      this.highlightLayer = null;
    }
  }

  /**
   * Angular lifecycle hook when the service is destroyed
   * Remove observables
   */
  ngOnDestroy(): void {
    if (this.identifyObs) {
      this.identifyObs.unsubscribe();
    }
    if (this.identifyRunObs) {
      this.identifyRunObs.unsubscribe();
    }
    this.removeGraphics();
  }

  /**
   * allows the Identify stuff to create graphics without connecting to the graphics service because they need the style property
   * @param geometryType: geometry type to create
   * @param layer: string layer name to grab the correct graphics layer
   * @param geometry: Geometry object to be created
   */
  public createGraphics(geometryType, layer, geometry) {
    let graphicsLayer;
    let style;
    if (layer === 'highlight') {
      graphicsLayer = this.highlightLayer;
      style = this.highlightStyle;
      this.highlightLayer.removeAll();
    } else {
      graphicsLayer = this.graphicsLayer;
      style = this.selectStyle;
    }
    this.graphics.createGraphics(geometryType, graphicsLayer, geometry, style);
  }

  /**
   * Sets up the identify observable
   * The observable watches for clicks on the map and then initializes the identify widget
   */
  public identifyInit() {
    if (!this.identifyObs) {
      this.identifyObs = this.map.getIdentifyObservable().subscribe( async (e) => {
        if (e) {
          this.grid.widgetOn('identify');
          if (!this.graphicsLayer) { this.graphicsLayer = await this.graphics.addGraphicsLayer('identify'); }
          if (!this.highlightLayer) { this.highlightLayer = await this.graphics.addGraphicsLayer('highlight'); }
          this.runIdentifyTask(e);
        } else {
          this.removeGraphics();
          this.grid.widgetOff('identify');
        }
      });
    }
  }

  /**
   * Runs the identify query
   * @param e: mouse click event
   */
  private async runIdentifyTask(e) {
    try {
      if (e && e.mapPoint) {
        let results = {};
        const currentView = this.map.getViewRef();
        // clears any graphics on the map from previous identifies
        if (this.graphicsLayer) { this.graphicsLayer.removeAll(); }
        else { this.graphicsLayer = await this.graphics.addGraphicsLayer('identify'); }
        if (this.highlightLayer) { this.highlightLayer.removeAll(); }
        else { this.highlightLayer = await this.graphics.addGraphicsLayer('highlight'); }
        // increments the click number
        this.identifyNumber++;
        this.resetIdentifyGlobals();
        // gets the service information for currently visible layers
        const services: any = this.toc.getVisibleLayerList();
        // sets up local variables to handle the services
        const serviceUrlArr: any = [];
        const serviceUrlSubArr: any = [];
        const extent = currentView.extent;
        let index = 0;

        // sets the mouse cursor to busy signal
        this.subIndex = 0;
        this.loading = services.length !== 0;
        this.identifyRunObs.next(null);

        // creates a buffer around the click to make clicking on points easier
        const buffer = currentView.scale / 500;     
        const envelope = {
          xmin: e.mapPoint.x - buffer,
          ymin: e.mapPoint.y - buffer,
          xmax: e.mapPoint.x + buffer,
          ymax: e.mapPoint.y + buffer
        };

        let checkedFlag = false;
        // finds the service number of all the layers clicked on,
        // and checks to see if they're actually visible on the map
        for (const service of services) {
          // this.subIndex = services.length;
          let temp: number;
          if (service && (service.table || service.rasterQuery)) {
            if (service.isChecked && (service.isParentChecked || service.isParentChecked === undefined)) {
              checkedFlag = true;
              this.subIndex++;
              let serviceType = 'web'; // default is web service layer
              if (service.isLocalFile) {
                serviceType = 'local';  // local data layer
              }
              switch (serviceType) {
                case 'web':
                  // Handle web layers
                  if (service.visibleLayersArr) {
                    for (const visibleLayersArr of service.visibleLayersArr) {
                      // the lowest number is the parent number, so function looks for highest number
                      if (temp) {
                        temp = Math.max(temp, parseInt(visibleLayersArr, 10));
                      } else {
                        temp = parseInt(visibleLayersArr, 10);
                      }
                    }
                    // parses out the service url to an array, and the layerID to another
                    serviceUrlArr[index] = service.url;
                    serviceUrlSubArr[index] = temp;
                    // checks for outfields in config file
                    const outfields: any = service.outFields ? service.outFields : '*';
                    if (service.rasterQuery) {
                      const rasterResults =
                        await this.rasterIdentify(currentView.size, e, extent, index, service.name, serviceUrlArr, serviceUrlSubArr,
                          service.token);
                      const tmp = this.parse.formatRasterIdentify(rasterResults);
                      if (tmp) {
                        tmp.identifyNumber = this.identifyNumber;
                        this.createIdentifyObject(tmp);
                      }
                    } else {
                      results = await this.serviceIdentify(service, serviceUrlArr, serviceUrlSubArr, index, envelope, outfields);
                      index--;
                    }
                    // increments the counter to account for layers that are checked off
                    index++;
                  } else {
                    if (this.identifyDisplay.headers.length === 0) {
                      this.loading = false;
                      this.blankIdentify = true;
                    }
                  }
                  break;
                case 'local':
                  results = await this.localIdentify(service, envelope);
                  break;
              }
            }
            if (results) {
              results = await this.parse.parseIdentify(results);
              this.createIdentifyObject(results);
            }
          }
          this.identifyRunObs.next(null);
        }  // end for each service
        if (!checkedFlag)  {
          this.loading = false;
          this.blankIdentify = true;
          this.identifyRunObs.next(null);
        }
      } else {
        console.log('No mapPoint in identify');
      }
    } catch (error) {
      this.loading = false;
      this.ga.exception(error, false);
      this.identifyRunObs.next(null);
    }
  }

  /**
   * Resets the identify objects on a new query run
   */
  public resetIdentifyGlobals() {
    this.identifyDisplay = {
      attributes: [],
      columns: {
        names: [],
        aliases: [],
        types: []
      },
      headers: []
    };
    this.identifyRun = false;
    this.identifyArr = [];
    this.identifyRelatedDisplay =  [];
    this.identifyIndex = 0;
  }

  /**
   * Formats and sends the identify query for a web service layer
   * @param service: service object
   * @param serviceUrlArr: Url of service to querry
   * @param serviceUrlSubArr: sub url
   * @param index: index of service
   * @param envelope: Buffered mouse click position
   * @param outfields: fields to query against
   */
  private async serviceIdentify(service, serviceUrlArr,  serviceUrlSubArr, index, envelope, outfields) {
    try {
      const tmpQueryObj: QueryObject = {};
      tmpQueryObj.layerTaskUrl = serviceUrlArr[index] + '/' + serviceUrlSubArr[index];
      if (service.layerType === 'feature') {
        // if this is a feature layer, the layerid is already included in the url
        tmpQueryObj.layerTaskUrl = serviceUrlArr[index];
      }

      tmpQueryObj.name = service.name;
      tmpQueryObj.layerElem = service;
      tmpQueryObj.formatFields = service.formatFields;
      tmpQueryObj.relateObj = service.relateObj;
      tmpQueryObj.parent = service.parentName;
      tmpQueryObj.geometry =  envelope.xmin + ',' + envelope.ymin + ','
        + envelope.xmax + ',' + envelope.ymax;
      tmpQueryObj.returnGeometry = true;
      tmpQueryObj.outFields = outfields;
      tmpQueryObj.queryID = 'identifyFeatures';
      tmpQueryObj.queryMethod = 'POST';
      tmpQueryObj.displayFieldName = service.displayField;
      tmpQueryObj.identifyType = service.identifyType;
      tmpQueryObj.identifyNumber = this.identifyNumber;
      if (service.token) {
        // Set token if needed
        tmpQueryObj.token = service.token;
      }
      return await this.query.queryData(tmpQueryObj);
    } catch (error) {
      this.ga.exception(error, false);
    }
  }

  /**
   * Sets up raster identify against the query service
   * @param size: Current view object reference size method
   * @param e: Mouse click
   * @param extent: current extent of the map
   * @param index: Query index
   * @param name: Name of the service
   * @param serviceUrlArr: URL
   * @param subArr: map layer
   */
  private async rasterIdentify(size, e, extent, index, name, serviceUrlArr, subArr, token) {
    try {
      const tmpQueryObj: QueryObject = {};
      tmpQueryObj.name = name;
      tmpQueryObj.geometry = e.mapPoint.x + ',' + e.mapPoint.y;
      tmpQueryObj.viewSize = size;
      tmpQueryObj.extent = extent;
      tmpQueryObj.returnGeometry = false;
      tmpQueryObj.layerType = 'Raster Layer';
      tmpQueryObj.layerID = subArr;
      tmpQueryObj.layerTaskUrl = serviceUrlArr[index];
      if (token) {
        tmpQueryObj.token = token;
      }
      return await this.query.queryData(tmpQueryObj);
    } catch (error) {
      this.ga.exception(error, false);
    }
  }

  /**
   * Formats the query for a local graphics layer
   * @param service: the layer to identify
   * @param envelope: Buffered mouse click position
   */
  private async localIdentify(service, envelope) {
    try {
      // Handle local files
      const queryObj: QueryObject = {};
      queryObj.name = service.name;
      queryObj.geometry =
        envelope.xmin + ',' + envelope.ymin + ',' + envelope.xmax + ',' + envelope.ymax;
      queryObj.returnGeometry = true;
      queryObj.queryID = 'identifyFeatures';
      queryObj.queryMethod = 'GET';
      queryObj.returnType = 'FEATURES';
      queryObj.displayFieldName = service.displayField;
      queryObj.identifyType = service.identifyType;
      queryObj.objectIdField = service.objectIdField;
      queryObj.identifyNumber = this.identifyNumber;
      queryObj.layerElem = service;
      return await this.query.queryData(queryObj);
    } catch (error) {
      this.ga.exception(error, false);
    }
  }

  /**
   * Creates the identify object for the currently identified item
   * Adds graphics to the page showing where the identified item is
   * Checks for any related tables and sends out the queries for them
   * @param result: results of the identify query
   */
  private createIdentifyObject(result) {
    if (result && result !== 'blank') {
      this.subIndex--;
      if (this.subIndex >= 0) {
        this.loading = false;
      }
      if (result.identifyNumber === this.identifyNumber) {
        // adds result to the identify array
        this.identifyArr[this.identifyIndex] = result;
        this.identifyIndex++;

        // checks if identify is the first
        if (result.featureArr[0] && !this.identifyRun) {
          // turns off blank identify statement
          this.blankIdentify = false;

          // adds new information to the identify display
          this.identifyDisplay = {
            attributes: result.featureArr[0],
            columns: result.columns,
            headers: result.header
          };
          this.identifyRun = true;
          this.identifyArr[this.identifyIndex - 1].highlight = 0;

          // checks for related information
          if (result.relateObj) {
            this.identifyRelated(result, 0);
          }
          if (result.geometryType) {
            // creates the graphics for map display
            this.graphics.createGraphics(result.geometryType, this.highlightLayer, result.geometryArr[0], this.highlightStyle);
            for (const geometryArr of result.geometryArr) {
              this.graphics.createGraphics(result.geometryType, this.graphicsLayer, geometryArr, this.selectStyle);
            }
          }
        }
      } else {
        if (this.identifyDisplay.headers === []) {
          this.loading = false;
          this.blankIdentify = true;
        }
      }
    } else {
      if (this.identifyDisplay.headers.length === 0) {
        this.loading = false;
        this.blankIdentify = true;
      }
      this.subIndex--;
      if (this.subIndex >= 0) {
        this.loading = false;
      }
    }
  }

  /**
   * Queries a related table and processes the results
   * @param data: the data to query
   * @param index: index of the query
   */
  public async identifyRelated(data: any, index: any) {
    try {
      // blanks out the old array
      this.identifyRelatedDisplay = [];
      let objectids = 0;
      if (index) {
        objectids = index;
      }

      // creates the url for the observable
      for (let i = 0; i < data.relateObj.length; i++) {
        this.subIndex++;

        // finds date fields for the parse service to sort on
        let sortVar: any;
        if (data.relateObj[i].outFields) {
          for (const outFields of data.relateObj[i].outFields) {
            const regex = new RegExp(/date/g);
            const regex2 = new RegExp(/year/g);
            if (outFields.toLowerCase().match(regex)
              || outFields.toLowerCase().match(regex2)) {
              sortVar = outFields;
              break;
            }
          }
        }
        const queryObj: QueryObject = {};
        if (sortVar) {
          queryObj.orderByFields = sortVar;
        }
        queryObj.layerTaskUrl = data.url;
        queryObj.name = data.relateObj[i].alias;
        queryObj.formatFields = data.relateObj[i].formatFields;
        queryObj.relateObj = data.relateObj;
        queryObj.parent = data.name;
        queryObj.outFields = data.relateObj[i].outFields;
        queryObj.queryID = 'identifyRelateFeatures';
        queryObj.queryMethod = 'POST';
        queryObj.identifyType = data.relateObj[i].identifyType;
        queryObj.objectIds = data.objectids[objectids];
        queryObj.relationshipClassIDS = data.relateObj[i].ID;
        queryObj.identifyNumber = data.identifyNumber;
        queryObj.identifyOrder = i;
        if (data.relateObj[i].token) {
          queryObj.token = data.relateObj[i].token;
        }
        let result = await this.query.queryData(queryObj);
        result = this.parse.identifyRelatedFeatures(result);
        this.sortRelatedIdentify(result);
      }
    } catch (error) {
      this.ga.exception(error, false);
    }
  }

  /**
   * Adds related results to the identify array
   * Sorts the related array for the proper order
   * @param result: the results of the identify query
   */
  private sortRelatedIdentify(result) {
    if (result) {
      this.subIndex--;
      if (this.subIndex >= 0) {
        this.loading = false;
      }
      if (result.identifyNumber === this.identifyNumber) {
        if (result.attributes.length > 0 || result.borehole.length > 0) {
          this.identifyRelatedDisplay.push(result);
          this.identifyRelatedDisplay.sort((a: any, b: any) => {
            return a.identifyOrder - b.identifyOrder;
          });
        }
      }
    }
  }
}
